import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Singleton — one Pyodide instance for the page lifetime.
// Multiple components can call usePyodide(); they all share the same runtime
// and the same Python global namespace (notebook model).
// ---------------------------------------------------------------------------

let _instance = null;
let _promise = null;

const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/v0.26.4/full/pyodide.js';

// Python helpers injected once after Pyodide loads.
const SETUP_PY = `
import sys, io

class _StrCap:
    def __init__(self): self._buf = []
    def write(self, s): self._buf.append(str(s))
    def flush(self): pass
    def getvalue(self): return ''.join(self._buf)

# Shared lesson namespace — all notebook cells execute here.
def _run_cell(code):
    _out = _StrCap(); _err = _StrCap()
    _old_out, _old_err = sys.stdout, sys.stderr
    sys.stdout, sys.stderr = _out, _err
    _exc = None
    try:
        exec(compile(code, '<cell>', 'exec'), globals())
    except BaseException:
        import traceback as _tb
        _exc = _tb.format_exc()
    finally:
        sys.stdout, sys.stderr = _old_out, _old_err
    return _out.getvalue(), _err.getvalue(), _exc

# Isolated scratchpad namespace — never pollutes lesson globals.
_scratch_ns = {'__builtins__': __builtins__}

def _run_scratch(code):
    _out = _StrCap(); _err = _StrCap()
    _old_out, _old_err = sys.stdout, sys.stderr
    sys.stdout, sys.stderr = _out, _err
    _exc = None
    try:
        exec(compile(code, '<scratch>', 'exec'), _scratch_ns)
    except BaseException:
        import traceback as _tb
        _exc = _tb.format_exc()
    finally:
        sys.stdout, sys.stderr = _old_out, _old_err
    return _out.getvalue(), _err.getvalue(), _exc
`;

function _injectScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Pyodide — check network connection.'));
    document.head.appendChild(s);
  });
}

export function getPyodide() {
  if (_instance) return Promise.resolve(_instance);
  if (!_promise) {
    _promise = _injectScript(PYODIDE_CDN)
      .then(() => window.loadPyodide())
      .then((py) => { py.runPython(SETUP_PY); _instance = py; return py; })
      .catch((err) => { _promise = null; throw err; });
  }
  return _promise;
}

// Run code in the shared lesson namespace. Returns { stdout, stderr, error }.
export async function runCell(pyodide, code) {
  pyodide.globals.set('_user_code', code);
  const json = await pyodide.runPythonAsync(`
import json as _json
_o, _e, _x = _run_cell(_user_code)
_json.dumps([_o, _e, _x])
`);
  const [stdout, stderr, error] = JSON.parse(json);
  return { stdout, stderr, error };
}

// Run code in the isolated scratchpad namespace.
export async function runScratch(pyodide, code) {
  pyodide.globals.set('_scratch_code', code);
  const json = await pyodide.runPythonAsync(`
import json as _json
_o, _e, _x = _run_scratch(_scratch_code)
_json.dumps([_o, _e, _x])
`);
  const [stdout, stderr, error] = JSON.parse(json);
  return { stdout, stderr, error };
}

// Returns filtered lesson namespace: { varName: { value: repr, type: typename } }
export async function getNamespace(pyodide) {
  const json = await pyodide.runPythonAsync(`
import json as _json
_ns = {}
for _k in list(globals().keys()):
    if _k.startswith('_'):
        continue
    _v = globals()[_k]
    try:
        _ns[_k] = {'value': repr(_v), 'type': type(_v).__name__}
    except Exception:
        _ns[_k] = {'value': '…', 'type': '?'}
_json.dumps(_ns)
`);
  return JSON.parse(json);
}

// ---------------------------------------------------------------------------
// Hook — thin wrapper over the singleton for React components.
// ---------------------------------------------------------------------------
export function usePyodide() {
  const [state, setState] = useState({ pyodide: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    getPyodide()
      .then((py) => {
        if (!cancelled) setState({ pyodide: py, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ pyodide: null, loading: false, error: err.message || 'Failed to load Python runtime.' });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
}
